import {BaseTimelineView} from "./baseTimelineView";
import {TimelineEntries} from "../timelineTypes";
import {getISODate, Temporal} from "@/shared/utils/datefunctions";
import {TimelineWeekHorizontalInteractions} from "./timelineWeekHorizontalInteractions";

/**
 * Web component for displaying a weekly view of the timeline.
 * The days of the current week are retrieved and displayed horizontally in a weekly view. By default, standard working days are provided (e.g. Monday to Friday) others only if they contain entries.
 * If working time feature is available, <code>TimelineWeek</code> are generated and appended for each day in the week.
 * Otherwise - if project time is available - <code>ProjectTimeTimelineDay</code> is generated to display the timeline for project time only.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class TimelineWeekView extends BaseTimelineView {
	private _date: Temporal.PlainDate = Temporal.Now.plainDateISO();
	private horizontalInteractions: TimelineWeekHorizontalInteractions | null = null;

	public override connectedCallback(): void {
		super.connectedCallback();

		// initialize horizontal interactions - drag-to-scroll, cut-day indicators when there is not enough space and week is partially invisible
		this.horizontalInteractions = new TimelineWeekHorizontalInteractions(
			this,
			this.timelineDaysContainer,
			() => this.timelineDayComponents.map(component => ({element: component, date: component.timelineDay.date}))
		);

		this.horizontalInteractions.attach();
	}

	public override disconnectedCallback(): void {
		this.horizontalInteractions?.detach();
		this.horizontalInteractions = null;
		super.disconnectedCallback();
	}

	/**
	 * Loads the weekly timeline for the specified user and date.
	 */
	public load(userId: number, date: Temporal.PlainDate): void {
		this.userId = userId;
		this.date = date;

		this.generateTimeline(this.date, true)
			.done((timelineEntries: TimelineEntries) => {
				const {workingTimeTimelineDays, projectTimeTimelineDays, combinedRecordingStatus} = timelineEntries;

				this.populateTimeline(
					workingTimeTimelineDays,
					projectTimeTimelineDays,
					combinedRecordingStatus,
					true
				);
				this.horizontalInteractions?.requestViewportUpdate();
			});
	}

	/**
	 * Reloads the weekly timeline while keeping the current scroll position.
	 */
	public reload(): void {
		const positionBeforeReload = window.scrollY;

		this.generateTimeline(this.date, true)
			.done((timelineEntries: TimelineEntries) => {
				const {workingTimeTimelineDays, projectTimeTimelineDays, combinedRecordingStatus} = timelineEntries;

				this.populateTimeline(
					workingTimeTimelineDays,
					projectTimeTimelineDays,
					combinedRecordingStatus,
					false
				);
				this.horizontalInteractions?.requestViewportUpdate();
				window.scrollTo({left: 0, top: positionBeforeReload, behavior: "auto"});
			});
	}

	private generateTimeline(startDate: Temporal.PlainDate, showLoadingSkeleton = false): JQueryXHR {
		return this.requestTimelineEntries(
			"timeline/generateWeeklyTimelineEntries.json",
			{
				date: getISODate(startDate)
			},
			showLoadingSkeleton
		);
	}

	get date(): Temporal.PlainDate {
		return this._date;
	}

	set date(value: Temporal.PlainDate) {
		this._date = value;
	}
}
